import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Api, { formatUrl } from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Input, Message } from 'semantic-ui-react';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { listActions, getTypes } from '@plone/volto/actions';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import { useClient } from '@plone/volto/hooks/client/useClient';

/**
 * Tela "Auditoria do acervo" — dispara o lote de auditoria das notícias (News e
 * Noticia) por SETOR (unidade de origem) e por tipo, e acompanha o progresso.
 *
 * - O administrador roda qualquer setor e também o portal inteiro.
 * - O gestor de unidade (e-mail em "emails_gestores") roda só os seus setores.
 * Quem define os setores disponíveis é o backend (`GET /@a11y-acervo-escopo`).
 *
 * O lote roda no backend em *fatias* (background). Aqui apenas iniciamos
 * (`POST /@a11y-auditar-acervo {acao: iniciar, unidades, portal_type}`) e
 * consultamos o progresso (`GET /@a11y-auditar-acervo`) até concluir — a request
 * nunca fica presa.
 */

const TODOS = '__todos__';

// O `mod` casa com os modificadores de `.mpf-a11y-pill` em
// `theme/styles/components/internal/_a11y.scss`.
const STATUS = {
  conforme: { label: 'Conforme', mod: 'aprovado' },
  pendente: { label: 'Pendente', mod: 'ressalva' },
  em_analise: { label: 'Em análise', mod: 'analise' },
  em_correcao: { label: 'Em correção', mod: 'analise' },
};

const pillClass = (mod) => `mpf-a11y-pill mpf-a11y-pill--${mod}`;

function notaLabel(nota) {
  if (typeof nota !== 'number') return '—';
  return nota.toFixed(1);
}

const STATUS_LABEL = {
  em_andamento: 'Em andamento',
  concluido: 'Concluído',
  ocioso: 'Ocioso',
};

const AuditarAcervo = () => {
  const { pathname } = useLocation();
  const isClient = useClient();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token);
  const [batchSize, setBatchSize] = useState('20');
  const [escopo, setEscopo] = useState(null);
  const [setor, setSetor] = useState('');
  const [tipo, setTipo] = useState('');

  // Carrega as ações/tipos da raiz para o Toolbar renderizar (rota virtual).
  useEffect(() => {
    if (token) {
      dispatch(listActions(''));
      dispatch(getTypes(''));
    }
  }, [token, dispatch]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const url = `${formatUrl('/')}@a11y-auditar-acervo`;
  const escopoUrl = `${formatUrl('/')}@a11y-acervo-escopo`;

  // Setores que o usuário pode auditar (o backend decide: admin x gestor).
  useEffect(() => {
    if (!token) return;
    new Api()
      .get(escopoUrl)
      .then((res) => {
        setEscopo(res);
        const setores = res?.setores || [];
        // Administrador começa em "portal inteiro"; gestor, no primeiro setor seu.
        setSetor(res?.portal_inteiro ? TODOS : setores[0]?.codigo || '');
      })
      .catch((err) => {
        const body = err?.response?.body;
        setEscopo({
          erro: body?.message || 'Sem acesso à auditoria do acervo.',
        });
      });
  }, [token, escopoUrl]);

  const fetchStatus = useCallback(async () => {
    try {
      const api = new Api();
      const res = await api.get(url);
      setProgress(res);
      return res;
    } catch (err) {
      const body = err?.response?.body;
      setError(
        body?.message ||
          body?.error?.message ||
          err?.message ||
          'Não foi possível consultar o progresso do lote.',
      );
      return null;
    }
  }, [url]);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    const res = await fetchStatus();
    if (res && res.status === 'em_andamento') {
      timer.current = setTimeout(poll, 4000);
    } else {
      setLoading(false);
    }
  }, [fetchStatus]);

  const handleRun = useCallback(async () => {
    if (
      setor === TODOS &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Isto audita TODAS as notícias do portal e pode levar muito tempo. Continuar?',
      )
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const api = new Api();
      const batch = batchSize.trim() ? parseInt(batchSize.trim(), 10) : null;
      const res = await api.post(url, {
        data: {
          acao: 'iniciar',
          batch_size: batch,
          unidades: setor && setor !== TODOS ? [setor] : [],
          portal_type: tipo ? [tipo] : [],
        },
      });
      setProgress(res);
      if (res?.status === 'em_andamento') {
        timer.current = setTimeout(poll, 2000);
      } else {
        setLoading(false);
      }
    } catch (err) {
      const body = err?.response?.body;
      setError(
        body?.message ||
          body?.error?.message ||
          err?.message ||
          'Não foi possível iniciar a auditoria do acervo.',
      );
      setLoading(false);
    }
  }, [url, batchSize, setor, tipo, poll]);

  useEffect(() => {
    if (token) {
      fetchStatus().then((res) => {
        if (res && res.status === 'em_andamento') {
          setLoading(true);
          timer.current = setTimeout(poll, 2000);
        }
      });
    }
    return stopPolling;
  }, [token, fetchStatus, poll, stopPolling]);

  if (!token) {
    return (
      <div className="mpf-a11y-page">
        <div className="mpf-a11y-empty">
          <p className="mpf-a11y-empty__title">
            Você precisa entrar para acessar esta tela
          </p>
        </div>
      </div>
    );
  }

  const setores = escopo?.setores || [];
  const setorAtual = setores.find((x) => x.codigo === setor);
  // No recorte por setor só entram tipos com unidade de origem; no portal
  // inteiro, todos os configurados.
  const tiposDisponiveis = (escopo?.tipos || []).filter(
    (t) => setor === TODOS || t.por_setor,
  );
  const contar = (x) => (tipo ? x?.por_tipo?.[tipo] ?? 0 : x?.total ?? 0);
  const porTipoPortal = escopo?.por_tipo_portal || {};
  const aAuditar =
    setor === TODOS
      ? tipo
        ? porTipoPortal[tipo] ?? 0
        : Object.values(porTipoPortal).reduce((acc, n) => acc + n, 0)
      : contar(setorAtual);
  const outroEscopo = Boolean(progress?.outro_escopo);
  const nomeSetor = (cod) =>
    setores.find((x) => x.codigo === cod)?.titulo || cod.toUpperCase();
  const nomeTipo = (id) =>
    (escopo?.tipos || []).find((t) => t.id === id)?.title || id;
  const lote =
    progress?.status && progress.status !== 'ocioso' ? progress : null;
  const descricaoLote = lote
    ? `${
        lote.unidades?.length
          ? lote.unidades.map(nomeSetor).join(', ')
          : 'Portal inteiro'
      } · ${lote.tipos?.length === 1 ? nomeTipo(lote.tipos[0]) : 'todos os tipos'}`
    : '';

  const itens = progress?.itens || [];
  const erros = progress?.erros || [];
  const total = progress?.total || 0;
  const processados = progress?.processados || 0;
  const pct = total > 0 ? Math.round((processados / total) * 100) : 0;

  return (
    <div className="mpf-a11y-page">
      <h1 className="mpf-a11y-page__title">Auditoria do acervo</h1>
      <p className="mpf-a11y-page__subtitle">
        Roda a verificação de acessibilidade das notícias em segundo plano, em
        blocos, e atualiza a nota, a situação e o prazo de cada conteúdo.
        Escolha o setor e o tipo.{' '}
        {escopo?.admin
          ? 'Como administrador, você também pode auditar o portal inteiro.'
          : 'Você audita os setores pelos quais é gestor.'}{' '}
        Pode sair desta tela: o lote continua no servidor.
      </p>

      {escopo?.erro && (
        <Message warning>
          <Message.Header>Sem acesso</Message.Header>
          <p>{escopo.erro}</p>
        </Message>
      )}

      <div className="mpf-a11y-controls">
        <div className="mpf-a11y-field">
          <label className="mpf-a11y-field__label" htmlFor="a11y-setor">
            Setor
          </label>
          <select
            id="a11y-setor"
            className="mpf-a11y-select"
            value={setor}
            onChange={(e) => {
              const valor = e.target.value;
              setSetor(valor);
              // Ao trocar para um setor, um tipo sem unidade de origem deixa
              // de valer e é limpo.
              if (valor !== TODOS && tipo) {
                const escolhido = (escopo?.tipos || []).find(
                  (t) => t.id === tipo,
                );
                if (escolhido && !escolhido.por_setor) setTipo('');
              }
            }}
            disabled={loading || !setores.length}
          >
            {escopo?.portal_inteiro && (
              <option value={TODOS}>Portal inteiro (todos os setores)</option>
            )}
            {setores.map((x) => (
              <option key={x.codigo} value={x.codigo}>
                {`${x.codigo.toUpperCase()} — ${x.titulo} (${contar(x)})`}
              </option>
            ))}
          </select>
        </div>
        <div className="mpf-a11y-field">
          <label className="mpf-a11y-field__label" htmlFor="a11y-tipo">
            Tipo
          </label>
          <select
            id="a11y-tipo"
            className="mpf-a11y-select"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            disabled={loading}
          >
            <option value="">Todos os tipos</option>
            {tiposDisponiveis.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>
        <div className="mpf-a11y-field">
          <label className="mpf-a11y-field__label" htmlFor="a11y-batch">
            Tamanho do bloco (opcional)
          </label>
          <Input
            id="a11y-batch"
            type="number"
            min="1"
            placeholder="20"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            disabled={loading}
          />
        </div>
        <MPFButton
          variant="primary"
          size="m"
          loading={loading}
          disabled={loading || !escopo || Boolean(escopo.erro) || !setor}
          onClick={handleRun}
        >
          {loading
            ? 'Auditando…'
            : setor === TODOS
              ? 'Rodar no portal inteiro'
              : 'Rodar auditoria do setor'}
        </MPFButton>
        {!loading && progress?.status === 'em_andamento' && (
          <MPFButton
            variant="outline"
            size="m"
            onClick={() => {
              setLoading(true);
              poll();
            }}
          >
            Retomar progresso
          </MPFButton>
        )}
      </div>

      {escopo && !escopo.erro && (
        <p className="mpf-a11y-hint">
          {aAuditar} notícia{aAuditar === 1 ? '' : 's'} a auditar na seleção
          atual.
        </p>
      )}

      {outroEscopo && (
        <Message info>
          <Message.Header>Auditoria de outro setor em andamento</Message.Header>
          <p>
            Há um lote em execução fora dos seus setores. Você poderá iniciar o
            seu quando ele terminar.
          </p>
        </Message>
      )}

      {lote && !outroEscopo && (
        <p className="mpf-a11y-hint">
          Lote atual: <strong>{descricaoLote}</strong>
        </p>
      )}

      {error && (
        <Message negative>
          <Message.Header>Falha na auditoria</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {progress && !error && (
        <>
          <div className="mpf-a11y-summary mpf-a11y-summary--tres">
            <div className="mpf-a11y-stat">
              <span className="mpf-a11y-stat__num">{total}</span>
              <span className="mpf-a11y-stat__label">itens no lote</span>
            </div>
            <div className="mpf-a11y-stat">
              <span className="mpf-a11y-stat__num">{processados}</span>
              <span className="mpf-a11y-stat__label">
                processados · {STATUS_LABEL[progress.status] || progress.status}
              </span>
            </div>
            <div className="mpf-a11y-stat">
              <span
                className={`mpf-a11y-stat__num${erros.length ? ' mpf-a11y-stat__num--erro' : ''}`}
              >
                {erros.length}
              </span>
              <span className="mpf-a11y-stat__label">erros</span>
            </div>
          </div>

          {total > 0 && (
            <div
              className="mpf-a11y-progress"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="mpf-a11y-progress__bar"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}

          {itens.length > 0 && (
            <>
              <p className="mpf-a11y-section-title">Conteúdos auditados</p>
              <div className="mpf-a11y-list">
                {itens
                  .slice(-50)
                  .reverse()
                  .map((item) => {
                    const status = STATUS[item.status] || null;
                    const appPath = flattenToAppURL(item.url);
                    return (
                      <div key={item.url} className="mpf-a11y-row">
                        <UniversalLink
                          href={`${appPath}/edit`}
                          className="mpf-a11y-row__title"
                        >
                          {item.title || item.url}
                        </UniversalLink>
                        {status && (
                          <span className={pillClass(status.mod)}>
                            {status.label}
                          </span>
                        )}
                        <span className="mpf-a11y-row__score">
                          {notaLabel(item.score)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </>
          )}

          {erros.length > 0 && (
            <>
              <p className="mpf-a11y-section-title">Falhas</p>
              <div className="mpf-a11y-list">
                {erros.slice(-50).map((e) => (
                  <div
                    key={e.url || e.uid}
                    className="mpf-a11y-row mpf-a11y-row--erro"
                  >
                    <span className="mpf-a11y-row__erro-text">
                      {e.url || e.uid} — {e.erro}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {itens.length === 0 &&
            erros.length === 0 &&
            progress.status !== 'em_andamento' && (
              <div className="mpf-a11y-empty">
                <p className="mpf-a11y-empty__text">Nenhum item processado.</p>
              </div>
            )}
        </>
      )}

      <p className="mpf-a11y-note">
        O lote é assíncrono: roda em blocos no servidor (também pelo cron) e não
        trava o portal. Apenas um lote roda por vez. O progresso fica salvo e
        pode ser retomado.
      </p>

      {isClient &&
        createPortal(
          <Toolbar pathname={pathname} inner={<span />} />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

export default AuditarAcervo;
